import Formsy from 'formsy-react';
import React, { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Component, NewsletterSubmitStatus } from '../../types/schema';
import { getNewsletterSubscribeResponse } from '../../store/selectors/user.selectors';
import { subscribeToNewsletter } from '../../store/actions/user.actions';
import { withCssModulesClassNames } from '../../common/nextMigrationHelpers';
import { Nullable } from '../../types/common';
import { default as styles } from '../../scss/components/Newsletter.module.scss';
import NewsletterExpanded from './NewsletterExpanded';
import Input from './fields/Input';

const classNames = withCssModulesClassNames(styles);

export interface NewsletterOnSubmitProps {
  city: Nullable<string>;
  country: string;
  email?: string;
  phone?: string;
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  state: Nullable<string>;
  subscribe: boolean;
}

export interface NewsletterProps
  extends Partial<
  Pick<Component, 'header' | 'richTextCopy' | 'images' | 'copy' | 'subHeader' | 'type' >
  > {
  nextApp?: boolean;
}

const Newsletter = ({ header, subHeader, ...props }: NewsletterProps) => {
  const dispatch = useDispatch();

  const newsletterSubscribeResponse: string = useSelector(
    getNewsletterSubscribeResponse,
  );
  const [isRequired, setIsRequired] = useState(true);

  const formRef = useRef<Nullable<Formsy>>(null);

  const submit = (data: NewsletterOnSubmitProps) => {
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value ?? value !== ''),
    );
    dispatch(
      subscribeToNewsletter({
        ...cleanData,
        url: document.location.href,
        subscriptionSource: subHeader,
        isFooterEmailCapture: !header,
      }),
    );
    formRef.current?.reset();
  };

  const onChange = ({ email, phone }: NewsletterOnSubmitProps) => {
    const newIsRequired = !email && !phone;

    if (newIsRequired !== isRequired) {
      setIsRequired(newIsRequired);
    }
  };

  if (!header) {
    return (
      <div id="subscribe" className={classNames('newsletter')}>
        {newsletterSubscribeResponse
        !== NewsletterSubmitStatus.FooterNewsletterSuccess ? (
          <Formsy
            className={classNames('newsletter__form')}
            autoComplete="off"
            onValidSubmit={submit}
            onChange={onChange}
          >
            <div className={classNames('form-fancy-wrapper', 'newsletter__field')}>
              <Input
                autocapitalize="none"
                autoComplete="email"
                type="email"
                name="email"
                id="email"
                inputmode="email"
                validations="isEmail"
                validationError="Invalid Email"
                label="Email address"
                required={isRequired}
              />
              <Input
                autocapitalize="none"
                autoComplete="phone"
                type="tel"
                name="phone"
                id="phone"
                inputmode="phone"
                validations={{
                  matchRegexp: /^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
                }}
                validationError="Invalid Phone"
                label="Phone number"
                required={isRequired}
              />
            </div>
            <button className={classNames('newsletter__submit')} type="submit">
              Subscribe
            </button>
            <p className={classNames('newsletter__disclaimer')}>
              By providing your phone number and/or email and clicking “SUBSCRIBE”
              above, you agree to receive recurring automated promotional
              messages from Burrow at the phone number and/or email used when
              signing up. Consent is not a condition of purchase. Msg frequency
              may vary. Text Msg & data rates may apply. Reply HELP for help and
              STOP to unsubscribe from texts.
              <br />
              See{' '}
              <a target="_blank" href="https://burrow.com/terms">
                Terms and Conditions
              </a>{' '}
              &{' '}
              <a target="_blank" href="https://privacy.burrow.com/">
                Privacy Policy
              </a>
              .
            </p>
          </Formsy>
          ) : (
          <p className="small">{newsletterSubscribeResponse}</p>
          )}
      </div>
    );
  }

  return (
    <NewsletterExpanded
      onSubmit={submit}
      header={header}
      {...props}
      ref={formRef}
    />
  );
};

export default Newsletter;
